import {
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import './utilities/axios';
import { useEffect, Suspense } from 'react';
import { peekPostIntent } from './lib/postIntent';
import { login, consumeRedirectAfterLogin, getToken } from './lib/session';

import HomeLanding from './pages/HomeLanding';
import OnboardingStep1 from './pages/onboarding/OnboardingStep1';
import OnboardingStep2 from './pages/onboarding/OnboardingStep2';
import OnboardingStep3 from './pages/onboarding/OnboardingStep3';
import Drafts from './pages/Drafts';
import Welcome from './pages/Welcome';
import BottomNavBar from './components/BottomNavBar';
import FileView from './components/FileView';
import Header from './components/Header';
import { ClikkleProvider } from './context/ClikkleContext';
import { ResourceMenuProvider } from './context/ResourceMenuContext';
import { UserProfileProvider } from './context/UserProfileContext';
import { MyFiles, ResourceViewer } from './pages';
import Loading from './components/Loading';
// import LoginForm from './pages/auth/Login';
import SignupForm from './pages/auth/Signup';
import ThemeContextProvider from './style/theme';
import { CssBaseline } from '@mui/material';
import SignupTrial from './pages/SignupTrial';
import TrialActivation from './pages/TrialActivate';

import MyCalendar from './pages/MyCalendar';
import Dashboard from './pages/Dashboard';
import RequireAuth from './pages/RequireAuth';
import PlannerGeneration from './pages/PlannerGeneration';
import PublishDestinations from './pages/PublishDestinations';
import SocialConnect from './pages/SocialConnect';
import BrandDetail from './pages/BrandDetail';

// helper – your existing rule
const isAuthed = () => !!localStorage.getItem('authToken');

/** Read tokens from the URL exactly once after social login, store them,
 *  and then jump to the intended page.
 *  Priority: ?redirectTo -> localStorage redirectAfterLogin -> pending intent (/generation) -> /dashboard
 */
function IntentResume() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Parse query
    const sp = new URLSearchParams(location.search);
    const userId = sp.get('userId');
    const refreshToken = sp.get('refreshToken');
    const explicitRedirect = sp.get('redirectTo');

    if (!userId || !refreshToken) return;

    // 1) persist the session (your login helper already writes to storage)
    login({ userId, refreshToken });

    // 2) compute where to go
    const fromStorage = consumeRedirectAfterLogin();
    const hadIntent = !!peekPostIntent();

    const target =
      explicitRedirect ||
      fromStorage ||
      (hadIntent ? '/generation' : '/dashboard');

    // 3) strip sensitive params from the URL, then navigate
    const { protocol, host, pathname } = window.location;
    window.history.replaceState({}, '', `${protocol}//${host}${pathname}`);
    navigate(target, { replace: true });
  }, [location.search, navigate]);

  return null;
}

const App = () => {
  const location = useLocation();

  const PUBLIC_STANDALONE = [
    '/login',
    '/signup',
    '/register',
    '/register/trialactivate',
  ];

  // Public standalones (no app chrome)
  // if (PUBLIC_STANDALONE.includes(location.pathname)) {
  //   return (
  //     <ThemeContextProvider>
  //       <CssBaseline />
  //       <IntentResume />
  //       <Suspense fallback={<Loading />}>
  //         {location.pathname === '/login' && <LoginForm />}
  //         {location.pathname === '/signup' && <SignupForm />}
  //         {location.pathname === '/register' && <SignupTrial />}
  //         {location.pathname === '/register/trialactivate' && (
  //           <TrialActivation />
  //         )}
  //       </Suspense>
  //     </ThemeContextProvider>
  //   );
  // }

  if (PUBLIC_STANDALONE.includes(location.pathname)) {
    return (
      <ThemeContextProvider>
        <CssBaseline />
        <IntentResume />
        <Suspense fallback={<Loading />}>
          {/* Use the combined login+signup page for both /login and /signup */}
          {['/login', '/signup'].includes(location.pathname) && <SignupForm />}

          {/* Trial registration & activation */}
          {location.pathname === '/register' && <SignupTrial />}
          {location.pathname === '/register/trialactivate' && (
            <TrialActivation />
          )}
        </Suspense>
      </ThemeContextProvider>
    );
  }

  // Public onboarding (no chrome)
  if (location.pathname.startsWith('/onboarding/')) {
    return (
      <ThemeContextProvider>
        <CssBaseline />
        <IntentResume />
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path='/onboarding/step-1' element={<OnboardingStep1 />} />
            <Route path='/onboarding/step-2' element={<OnboardingStep2 />} />
            <Route path='/onboarding/step-3' element={<OnboardingStep3 />} />
          </Routes>
        </Suspense>
      </ThemeContextProvider>
    );
  }

  // Public landing (no chrome). Also: if someone hits the old /generate, send them to /generation.
  if (location.pathname === '/' || location.pathname === '/generate') {
    if (location.pathname === '/generate') {
      // permanent move to the new flow
      return <Navigate to='/generation' replace />;
    }
    return (
      <ThemeContextProvider>
        <CssBaseline />
        <IntentResume />
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path='/' element={<HomeLanding />} />
          </Routes>
        </Suspense>
      </ThemeContextProvider>
    );
  }

  // Check if the user has a brand before allowing them to proceed
  // This check will replace the previous logic of checking localStorage
  const API_BASE = process.env.REACT_APP_API;
  const checkUserHasBrand = async () => {
    const token = getToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE}/brands/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error("User's brands fetch failed");
        return false;
      }

      const data = await response.json();
      return data.length > 0; // Returns true if brands exist
    } catch (error) {
      console.error('Error checking for brands:', error);
      return false;
    }
  };

  // Protected dashboard route
  if (location.pathname === '/dashboard') {
    const hasBrand = checkUserHasBrand(); // Check directly from the DB

    if (!hasBrand) {
      return <Navigate to='/onboarding/step-1' replace />;
    }

    if (!isAuthed())
      return <Navigate to='/register?redirectTo=/dashboard' replace />;

    return (
      <ThemeContextProvider>
        <CssBaseline />
        <IntentResume />
        <Suspense fallback={<Loading />}>
          <Dashboard />
        </Suspense>
      </ThemeContextProvider>
    );
  }

  // Protected welcome
  if (location.pathname === '/welcome') {
    const brand = JSON.parse(localStorage.getItem('onboarding.brand') || '{}');
    if (!brand.businessName)
      return <Navigate to='/onboarding/step-1' replace />;
    if (!isAuthed())
      return <Navigate to='/register?redirectTo=/welcome' replace />;

    return (
      <ThemeContextProvider>
        <CssBaseline />
        <IntentResume />
        <Welcome />
      </ThemeContextProvider>
    );
  }

  // Everything else – define the protected routes we added
  return (
    <ThemeContextProvider>
      <CssBaseline />
      <IntentResume />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route
            path='/mycalendar'
            element={
              <RequireAuth>
                <MyCalendar />
              </RequireAuth>
            }
          />
          {/* NEW flow: the only generator route we support */}
          <Route
            path='/generation'
            element={
              <RequireAuth>
                <PlannerGeneration />
              </RequireAuth>
            }
          />
          <Route
            path='/publish/:id'
            element={
              <RequireAuth>
                <PublishDestinations />
              </RequireAuth>
            }
          />
          {/* ✅ PROTECTED SOCIAL CONNECT ROUTE */}
          <Route
            path='/social-connect'
            element={
              <RequireAuth>
                <SocialConnect />
              </RequireAuth>
            }
          />

          <Route
            path='/brands/:id' // This dynamic route will capture the brand id
            element={
              <RequireAuth>
                <BrandDetail />
              </RequireAuth>
            }
          />

          {/* (legacy file routes left intact if you still need them) */}
          <Route path='/file/:fileId/:userId' element={<FileView />} />
          <Route path='/view' element={<ResourceViewer />} />

          {/* Fallback to landing */}
          <Route path='*' element={<HomeLanding />} />
        </Routes>
      </Suspense>
    </ThemeContextProvider>
  );
};

export default App;
